import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AcademicCalendar = ({ events }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date()?.getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date()?.getFullYear());

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getEventsForMonth = () => {
    return events?.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate?.getMonth() === selectedMonth && eventDate?.getFullYear() === selectedYear;
    })?.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getEventTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'exam':
        return 'bg-blue-300 text-red-800 border-red-200';
      case 'holiday':
        return 'bg-red-100 text-green-800 border-green-200';
      case 'admission':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'event':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-green-200 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short'
    });
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const monthEvents = getEventsForMonth();

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Calendar" size={20} />
          <span>Academic Calendar</span>
        </h3>
        <div className="flex items-center space-x-2 flex-wrap justify-center sm:justify-end">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            onClick={() => window.open('/assets/academic-calendar.pdf', '_blank')}
          >
            Download PDF
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="ChevronLeft"
            onClick={() => navigateMonth('prev')}
          />
          <span className="font-medium text-foreground min-w-[140px] text-center">
            {months?.[selectedMonth]} {selectedYear}
          </span>
          <Button
            variant="ghost"
            size="sm"
            iconName="ChevronRight"
            onClick={() => navigateMonth('next')}
          />
        </div>
      </div>
      <div className="space-y-3">
        {monthEvents?.length > 0 ? (
          monthEvents?.map((event) => (
            <div
              key={event?.id}
              className={`p-4 rounded-lg border ${getEventTypeColor(event?.type)} transition-all duration-200 hover:shadow-sm`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-medium">{event?.title}</span>
                    <span className="text-xs px-2 py-1 rounded-full bg-white/50">
                      {event?.type}
                    </span>
                  </div>
                  <p className="text-sm opacity-80 mb-2">{event?.description}</p>
                  <div className="flex items-center space-x-2 sm:space-x-4 text-xs flex-wrap">
                    <span className="flex items-center space-x-1">
                      <Icon name="Calendar" size={12} />
                      <span>{formatDate(event?.date)}</span>
                    </span>
                    {event?.time && (
                      <span className="flex items-center space-x-1">
                        <Icon name="Clock" size={12} />
                        <span>{event?.time}</span>
                      </span>
                    )}
                    {event?.location && (
                      <span className="flex items-center space-x-1">
                        <Icon name="MapPin" size={12} />
                        <span>{event?.location}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Calendar" size={48} className="mx-auto mb-3 opacity-50" />
            <p>No events scheduled for {months?.[selectedMonth]} {selectedYear}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademicCalendar;